// The Sessions roster grid: rows = doctors (or locations), columns = weekdays
// (configured first-day order, optional hideSunday), cells = session chips.
//
//   • Template mode: chips are the recurring blocks whose weekday mask covers
//     the column; monthly rotations get a cadence badge.
//   • Week mode: chips are the expanded occurrences of the visible week
//     (overrides applied — ghosts, moves, relocations).
//
// Scrolling uses ScrollArea (app-wide convention: never raw overflow-*).

import { useMemo } from "react";
import { CalendarPlus } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { HueAvatar } from "../slots-heatmap/heatmap-bits";
import { getVisibleWeekdayColumns } from "../slots-heatmap/heatmap-utils";
import { blockWeekdays, cadenceBadge } from "./sessions-utils";
import { SessionChip } from "./session-chip";

function formatBlockRange(block) {
  return `${block.startTime}–${block.endTime}`;
}

function formatOccurrenceRange(occ) {
  if (!occ.startAt || !occ.endAt) return "";
  return `${occ.startAt.toFormat("HH:mm")}–${occ.endAt.toFormat("HH:mm")}`;
}

export function RosterMatrix({
  mode, // "template" | "week"
  groupBy, // "doctor" | "tenant"
  hideSunday = false,
  weekDates, // Luxon DateTimes (7), aligned to the configured first day
  blocks = [],
  occurrences = [],
  doctorOptions = [],
  tenantOptions = [],
  selectedDoctorIds,
  selectedTenantIds,
  isLoading = false,
  canManage = false,
  onChipClick, // (payload) => void — {block} in template mode, {occurrence, block} in week mode
  onAddToCell, // (rowEntityId, isoWeekday) => void — template mode only
}) {
  const columns = getVisibleWeekdayColumns(hideSunday);

  const nameOf = useMemo(() => {
    const doctorIndex = new Map(doctorOptions.map((opt) => [String(opt.id), opt.name]));
    const tenantIndex = new Map(tenantOptions.map((opt) => [String(opt.id), opt.name]));
    return {
      doctor: (id) => doctorIndex.get(String(id)) || String(id || ""),
      tenant: (id) => tenantIndex.get(String(id)) || String(id || ""),
    };
  }, [doctorOptions, tenantOptions]);

  const doctorFilter = useMemo(() => new Set(selectedDoctorIds || []), [selectedDoctorIds]);
  const tenantFilter = useMemo(() => new Set(selectedTenantIds || []), [selectedTenantIds]);

  const blockById = useMemo(() => {
    const map = new Map();
    for (const block of blocks) map.set(block.id, block);
    return map;
  }, [blocks]);

  // rows: [{entityId, entityName, cells: Map<iso, chipPayload[]>}]
  const rows = useMemo(() => {
    const rowMap = new Map();
    const rowFor = (entityId, entityName) => {
      let row = rowMap.get(entityId);
      if (!row) {
        row = { entityId, entityName, cells: new Map() };
        rowMap.set(entityId, row);
      }
      return row;
    };
    const push = (row, iso, payload) => {
      const list = row.cells.get(iso) || [];
      list.push(payload);
      row.cells.set(iso, list);
    };

    if (mode === "template") {
      for (const block of blocks) {
        if (doctorFilter.size > 0 && !doctorFilter.has(block.doctorId)) continue;
        if (tenantFilter.size > 0 && !tenantFilter.has(block.tenantId)) continue;
        const rowEntityId = groupBy === "tenant" ? block.tenantId : block.doctorId;
        const rowEntityName =
          groupBy === "tenant" ? nameOf.tenant(block.tenantId) : nameOf.doctor(block.doctorId);
        const row = rowFor(rowEntityId, rowEntityName);
        for (const iso of blockWeekdays(block)) {
          push(row, iso, { kind: "block", block, sortKey: block.startTime });
        }
      }
    } else {
      for (const occ of occurrences) {
        if (doctorFilter.size > 0 && !doctorFilter.has(occ.doctorId)) continue;
        if (tenantFilter.size > 0 && !tenantFilter.has(occ.tenantId) && !tenantFilter.has(occ.baseTenantId)) continue;
        const iso = occ.startAt?.weekday;
        if (!Number.isInteger(iso)) continue;
        const rowEntityId = groupBy === "tenant" ? occ.tenantId : occ.doctorId;
        const rowEntityName =
          groupBy === "tenant" ? nameOf.tenant(occ.tenantId) : nameOf.doctor(occ.doctorId);
        const row = rowFor(rowEntityId, rowEntityName);
        push(row, iso, {
          kind: "occurrence",
          occurrence: occ,
          block: blockById.get(occ.blockId) || null,
          sortKey: occ.startAt?.toFormat("HH:mm") || "",
        });
      }
    }

    const list = [...rowMap.values()];
    list.sort((a, b) => a.entityName.localeCompare(b.entityName));
    for (const row of list) {
      for (const cellList of row.cells.values()) {
        cellList.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      }
    }
    return list;
  }, [mode, groupBy, blocks, occurrences, blockById, doctorFilter, tenantFilter, nameOf]);

  const dateByIso = useMemo(() => {
    const map = new Map();
    for (const dt of weekDates || []) {
      if (dt?.isValid) map.set(dt.weekday, dt);
    }
    return map;
  }, [weekDates]);

  const today = useMemo(() => new Date(), []);
  const todayIso = ((today.getDay() + 6) % 7) + 1;
  const showToday =
    mode === "week" &&
    dateByIso.get(todayIso) &&
    dateByIso.get(todayIso).toISODate() === new Date().toISOString().slice(0, 10);

  const empty = !isLoading && rows.length === 0;

  return (
    <ScrollArea className="min-h-0 flex-1">
      <div className="relative px-5 pb-8 pt-3">
        {isLoading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Spinner className="size-4" /> Loading sessions…
          </div>
        ) : empty ? (
          <div className="mx-auto my-12 max-w-md rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {mode === "template"
              ? "No session blocks yet. Create one to start replacing the free-text roster."
              : "No sessions this week."}
          </div>
        ) : (
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="sticky top-0 z-10 w-44 min-w-44 rounded-tl-lg border-b border-border bg-background px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {groupBy === "tenant" ? "Location" : "Doctor"}
                </th>
                {columns.map((col) => {
                  const date = dateByIso.get(col.iso);
                  const isToday = showToday && col.iso === todayIso;
                  return (
                    <th
                      key={col.iso}
                      className={cn(
                        "sticky top-0 z-10 border-b border-border bg-background px-1.5 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider",
                        col.weekend ? "text-muted-foreground/60" : "text-muted-foreground",
                        isToday && "text-primary",
                      )}
                    >
                      <span>{col.long.slice(0, 3)}</span>
                      {mode === "week" && date ? (
                        <span
                          className={cn(
                            "ml-1 font-mono tabular-nums",
                            isToday ? "text-primary" : "text-muted-foreground/70",
                          )}
                        >
                          {date.toFormat("d")}
                        </span>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.entityId} className="group/row">
                  <td className="border-b border-border/60 px-2 py-2 align-top">
                    <div className="flex items-center gap-2">
                      <HueAvatar name={row.entityName} id={row.entityId} size={22} />
                      <div className="min-w-0">
                        <div className="truncate text-[12px] font-medium">{row.entityName}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {[...row.cells.values()].reduce((n, list) => n + list.length, 0)} session
                          {[...row.cells.values()].reduce((n, list) => n + list.length, 0) === 1 ? "" : "s"}
                          {mode === "template" ? "/wk" : ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  {columns.map((col) => {
                    const cell = row.cells.get(col.iso) || [];
                    return (
                      <td
                        key={col.iso}
                        className={cn(
                          "border-b border-l border-border/60 p-1 align-top",
                          col.weekend && "bg-muted/25",
                          showToday && col.iso === todayIso && "bg-primary/[0.04]",
                        )}
                      >
                        <div className="flex min-h-12 flex-col gap-1">
                          {cell.map((payload) => {
                            if (payload.kind === "block") {
                              const block = payload.block;
                              return (
                                <SessionChip
                                  key={`${block.id}:${col.iso}`}
                                  label={block.label}
                                  timeRange={formatBlockRange(block)}
                                  otherName={
                                    groupBy === "tenant"
                                      ? nameOf.doctor(block.doctorId)
                                      : nameOf.tenant(block.tenantId)
                                  }
                                  toneId={groupBy === "tenant" ? block.doctorId : block.tenantId}
                                  colorHex={block.colorHex}
                                  badge={cadenceBadge(block.recurrence)}
                                  onClick={onChipClick ? () => onChipClick(payload) : undefined}
                                />
                              );
                            }
                            const occ = payload.occurrence;
                            return (
                              <SessionChip
                                key={occ.key}
                                label={occ.label}
                                timeRange={formatOccurrenceRange(occ)}
                                otherName={
                                  groupBy === "tenant"
                                    ? nameOf.doctor(occ.doctorId)
                                    : nameOf.tenant(occ.tenantId)
                                }
                                toneId={groupBy === "tenant" ? occ.doctorId : occ.tenantId}
                                colorHex={occ.colorHex || payload.block?.colorHex}
                                occurrence={occ}
                                onClick={onChipClick ? () => onChipClick(payload) : undefined}
                              />
                            );
                          })}
                          {mode === "template" && canManage && onAddToCell ? (
                            <button
                              type="button"
                              onClick={() => onAddToCell(row.entityId, col.iso)}
                              title="Add a session block here"
                              className="flex items-center justify-center gap-1 rounded-md border border-dashed border-transparent px-1 py-0.5 text-[10px] text-transparent transition-colors hover:border-border hover:text-muted-foreground group-hover/row:text-muted-foreground/40"
                            >
                              <CalendarPlus className="size-3" /> Add
                            </button>
                          ) : null}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </ScrollArea>
  );
}
